import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import type { DealStatusFindInterface } from '@zaoblako/shared/modules/business/dto/deal-status/deal-status-find.dto';
import type { DealsGroupFindInterface } from '@zaoblako/shared/modules/business/dto/deals-group/deals-group-find.dto';
import {
  DealsGroupFormDto,
  type DealsGroupFormInterface
} from '@zaoblako/shared/modules/business/dto/deals-group/deals.group.form.dto';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { DealGroupsService } from '@zaoblako/web/core/services/deal/deal-groups.service';
import { DealStatusService } from '@zaoblako/web/core/services/deal/deal-status.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { DealGroupHistoryComponent } from '@zaoblako/web/modules/deal/dumbs/deal-group-form/deal-group-history/deal-group-history.component';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { DealMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/deal-multi-select/deal-multi-select.component';
import { UserGroupMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/user-group-multi-select/user-group-multi-select.component';
import { UserMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/user-multi-select/user-multi-select.component';
import { DealStatusSingleSelect } from '@zaoblako/web/shared/dumbs/dropdowns/deal-status-single-select/deal-status-single-select.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { CardModule } from 'primeng/card';
import { Observable } from 'rxjs';

@Component({
  standalone: true,
  selector: 'crm-deal-group-form',
  templateUrl: './deal-group-form.component.html',
  imports: [
    CommonModule,
    CardModule,
    InputTextComponent,
    JoinPipe,
    ButtonComponent,
    DealMultiSelectComponent,
    DealGroupHistoryComponent,
    SpinnerComponent,
    UserMultiSelectComponent,
    UserGroupMultiSelectComponent,
    DealStatusSingleSelect
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DealGroupFormComponent extends BaseFormComponent<DealsGroupFormInterface> implements OnInit {
  @Input() public readonly index: number;
  public group = <DealsGroupFormInterface>{};
  public dto = DealsGroupFormDto;
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public statuses: DealStatusFindInterface[] = [];

  public constructor(
    private readonly dealGroupService: DealGroupsService,
    private readonly dealStatusService: DealStatusService,
    public readonly formStoreService: FormStoreService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly errorService: ErrorService,
    private readonly confirmService: ConfirmDialogService
  ) {
    super();
  }

  public ngOnInit() {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    this.dealStatusService.findWithPagination().subscribe(({ items: statuses }) => {
      this.statuses = statuses;
      this.cdRef.markForCheck();
    });

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    this.dealGroupService
      .findById<DealsGroupFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId)
      .subscribe({
        next: (group) => {
          this.group = group;
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public saveDealGroup() {
    if (this.checkValidate(this.group).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.dealGroupService.update<DealsGroupFormInterface, DealsGroupFindInterface>(this.group).subscribe({
          next: (group) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage(`Проект "${this.group.name}" обновлен!`);
            this.formStoreService.saveNext(group, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.dealGroupService.create<DealsGroupFormInterface>(this.group).subscribe({
          next: (group) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Проект создан!');
            this.formStoreService.saveNext(group, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteDealGroup() {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить проект "${this.group.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.dealGroupService.deleteById(this.formStoreService.getState.forms[this.index]?.entityId).subscribe({
          next: () => {
            this.errorService.addSuccessMessage(`Проект "${this.group.name}" удален!`);
            this.formStoreService.deleteNext(this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    });
  }
}
